package ai.wagemix.net.models

import com.fsryan.foundation.models.FSStringEnumerated
import kotlin.time.Duration
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Instant

/** A shared mix's preview image: `previewStatus` on the shared mix and on its owner's record. */
enum class WMSharedMixPreviewStatus(override val stringValue: String): FSStringEnumerated {
    Pending("pending"),
    Ready("ready"),
    Failed("failed"),
}

/**
 * How long a preview may stay pending before its owner may ask for it again: longer than the API's render takes with
 * its retries, so a request in flight is never repeated.
 */
val WM_SHARED_MIX_PREVIEW_STALE_AFTER: Duration = 2.minutes

/**
 * One of the signed-in user's shared mixes, from their private record of it (`users/{uid}/sharedMixes/{sharedMixId}`),
 * which the API writes when the link is created and keeps in step with the preview image.
 */
interface WMOwnedShare {
    val sharedMixId: String
    /** The stored mix it was shared from; it may since have been changed or deleted. */
    val mixId: String?
    val title: String
    val badge: WMBadge
    val hourly: Boolean
    val purpose: WMSharedMixPurpose
    val shareableUrl: String
    val createdAt: Instant?
    /** `null` on a record the API wrote before it tracked previews. */
    val previewStatus: WMSharedMixPreviewStatus?
    val previewImageUrl: String?
    val previewRequestedAt: Instant?
    /** Whether the link works; the owner turns it off and on (planning/share-link-stats-plan.md rule 6). */
    val active: Boolean
    /** Whether the link's statistics are stored: only shares created since link statistics were added. */
    val statsTracked: Boolean

    companion object
}

/** `true` while the preview is pending and was requested within [WM_SHARED_MIX_PREVIEW_STALE_AFTER] of [now]. */
fun WMOwnedShare.isPreviewInProgress(now: Instant): Boolean {
    val requested = previewRequestedAt ?: return false
    return previewStatus == WMSharedMixPreviewStatus.Pending && now - requested < WM_SHARED_MIX_PREVIEW_STALE_AFTER
}

/**
 * The owner may ask for the preview again: its last render failed, or it has no image and nothing is rendering it
 * (a stale pending render, or a share from before the API tracked previews).
 */
fun WMOwnedShare.canRegeneratePreview(now: Instant): Boolean {
    return when {
        previewStatus == WMSharedMixPreviewStatus.Failed -> true
        previewImageUrl != null -> false
        else -> !isPreviewInProgress(now)
    }
}

fun WMOwnedShare.Companion.of(
    sharedMixId: String,
    mixId: String?,
    title: String,
    badge: WMBadge,
    hourly: Boolean,
    purpose: WMSharedMixPurpose,
    shareableUrl: String,
    createdAt: Instant?,
    previewStatus: WMSharedMixPreviewStatus?,
    previewImageUrl: String?,
    previewRequestedAt: Instant?,
    active: Boolean,
    statsTracked: Boolean,
): WMOwnedShare = WMOwnedShareData(
    sharedMixId = sharedMixId,
    mixId = mixId,
    title = title,
    badge = badge,
    hourly = hourly,
    purpose = purpose,
    shareableUrl = shareableUrl,
    createdAt = createdAt,
    previewStatus = previewStatus,
    previewImageUrl = previewImageUrl,
    previewRequestedAt = previewRequestedAt,
    active = active,
    statsTracked = statsTracked,
)

private data class WMOwnedShareData(
    override val sharedMixId: String,
    override val mixId: String?,
    override val title: String,
    override val badge: WMBadge,
    override val hourly: Boolean,
    override val purpose: WMSharedMixPurpose,
    override val shareableUrl: String,
    override val createdAt: Instant?,
    override val previewStatus: WMSharedMixPreviewStatus?,
    override val previewImageUrl: String?,
    override val previewRequestedAt: Instant?,
    override val active: Boolean,
    override val statsTracked: Boolean,
): WMOwnedShare

/**
 * How often one of the user's share links was opened (`users/{uid}/sharedMixStats/{sharedMixId}`), counted by the API
 * when it answers the link; readable by the owner on the Pro plan only (planning/share-link-stats-plan.md).
 */
interface WMShareStats {
    val sharedMixId: String
    /** Every time the link was opened, on or off. */
    val clickCount: Long
    /** The opens while the link was off, when visitors saw "deactivated" (part of [clickCount]). */
    val deactivatedClickCount: Long
    /** `null` until the link is first opened. */
    val lastClickedAt: Instant?

    companion object
}

fun WMShareStats.Companion.of(
    sharedMixId: String,
    clickCount: Long,
    deactivatedClickCount: Long,
    lastClickedAt: Instant?
): WMShareStats {
    return WMShareStatsData(sharedMixId, clickCount, deactivatedClickCount, lastClickedAt)
}

private data class WMShareStatsData(
    override val sharedMixId: String,
    override val clickCount: Long,
    override val deactivatedClickCount: Long,
    override val lastClickedAt: Instant?,
): WMShareStats
